<template>
	<section>
		<BaseAutocomplete
			ref="autocomplete"
			autofocus
			no-filter
			label="Agent Search"
			placeholder="Search for an Agent"
			clearable
			return-object
			:loading="loading"
			:items="results || []"
			@update:search-input="onSearchText"
			@change="chooseItem"
			@click:clear="clearResults"
		>
			<template v-if="empty" #no-data>
				<div class="p-2 font-medium">No matching agents</div>
			</template>
			<template #item="person">
				<AgentStaffNavigationCard
					v-bind="{ ...person, showDeepLinks, shouldEmit }"
					@choose="chooseItem"
				/>
			</template>
		</BaseAutocomplete>
		<template v-if="showHistory && history?.length > 0">
			<h3 class="mb-1 mt-4 text-xl font-semibold">Recently Viewed Agents</h3>

			<ul class="flex flex-col gap-2 @container">
				<AgentStaffNavigationCard
					v-for="person in history"
					:key="person.associate_id"
					v-bind="{ ...person, showDeepLinks, shouldEmit, tag: 'li' }"
					@choose="chooseItem"
				/>
			</ul>
		</template>
	</section>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useSearchHistory } from '@/composables/useSearchHistory';

import useAgentStore from '@/stores/agent/agent';
import AgentStaffNavigationCard from '@/components/common/AgentStaffNavigationCard.vue';
import BaseAutocomplete from '@/components/ui/BaseAutocomplete.vue';

const emit = defineEmits(['choose']);
const props = defineProps({
	dark: { type: Boolean, default: false },
	shouldEmit: { type: Boolean, default: false },
	showHistory: { type: Boolean, default: true },
	showDeepLinks: { type: Boolean, default: false },
});

const router = useRouter();
const agentStore = useAgentStore();
const { history, updateSearchHistory } = useSearchHistory();

const autocomplete = ref(null);
const search = ref('');
const results = ref(null);
const timeoutID = ref(null);

const loading = computed(() => Boolean(!results.value && search.value));
const empty = computed(() => results.value && results.value.length === 0);

function clearResults() {
	results.value = null;
	search.value = '';
	if (timeoutID.value) {
		clearTimeout(timeoutID.value);
	}
}

function onSearchText(val) {
	if (search.value === val) {
		return;
	}

	if (timeoutID.value) {
		clearTimeout(timeoutID.value);
	}

	results.value = null;
	search.value = val ? val.toLowerCase() : '';

	if (search.value) {
		timeoutID.value = setTimeout(async () => {
			const searchResults = await agentStore.search(search.value);

			// if the user has cleared the search box after the request was sent,
			// don't display the results.
			if (search.value) {
				results.value = searchResults;
			}
		}, 500);
	}
}

async function chooseItem(item) {
	if (item === null || item === undefined) {
		return;
	}

	await updateSearchHistory(item);
	autocomplete.value.toggleResultsContainer();
	autocomplete.value.searchInputText = '';
	if (props.shouldEmit) {
		emit('choose', item);
	} else {
		router.push(item.link);
	}
}

defineExpose({
	autocomplete,
});
</script>

<style scoped></style>
